"use client";

import { useEffect, useRef } from "react";

const services = [
  {
    step: "01",
    icon: "🎯",
    title: "Profile Evaluation & Country Selection",
    tagline: "Stop guessing. Start knowing.",
    desc: "Before you apply anywhere, we analyze where you actually stand — academically, financially, and professionally. Then we match you to the countries, universities, and programs where you have the highest chance of success and the best return on your investment.",
    highlight: "An honest, personalized roadmap",
  },
  {
    step: "02",
    icon: "🏫",
    title: "University Application Strategy",
    tagline: "The difference between accepted and rejected is often in the details.",
    desc: "We don't just fill out your forms. We build your application narrative — from shortlisting universities, to crafting your Statement of Purpose, to aligning your LORs, to ensuring every document tells a cohesive, compelling story.",
    highlight: "Applications that stand out",
  },
  {
    step: "03",
    icon: "📝",
    title: "SOP & Essay Writing Support",
    tagline: "Your SOP isn\'t a summary — it\'s your first impression.",
    desc: "A generic SOP gets ignored. A great one gets you in. Our writers work with you to uncover what makes your story worth telling — and then tell it in a way that resonates with admissions committees across the world.",
    highlight: "Your story, sharper & more persuasive",
  },
  {
    step: "04",
    icon: "🏦",
    title: "Scholarship Guidance",
    tagline: "Studying abroad doesn't have to drain your family's savings.",
    desc: "Most students leave scholarship money on the table because they don't know where to look or how to apply. We identify every scholarship you qualify for — merit-based, country-specific, university-funded — and help you build a strong application for each.",
    highlight: "A real shot at reducing your costs",
  },
  {
    step: "05",
    icon: "📋",
    title: "Visa Application & Preparation",
    tagline: "A rejected visa doesn't mean no. It means you needed better preparation.",
    desc: "Our 98% visa success rate exists because we treat visa applications like legal strategy — not paperwork. We prepare you for every document requirement, every interview question, and every potential red flag before the officer even opens your file.",
    highlight: "98% visa success rate",
  },
  {
    step: "06",
    icon: "✈️",
    title: "Pre-Departure & Settling-In Support",
    tagline: "Getting in is just the beginning.",
    desc: "Accommodation, travel, bank accounts, SIM cards, orientation — the logistics of actually moving abroad can be just as stressful as the applications. We stay with you through all of it, so you land prepared, not panicked.",
    highlight: "End-to-end support",
  },
];

export default function ServicesSection() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("visible");
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -60px 0px" }
    );
    ref?.current?.querySelectorAll(".reveal, .reveal-scale")?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section id="services" ref={ref} className="py-28 px-6 lg:px-10 bg-white">
      <div className="max-w-site mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="reveal flex justify-center mb-4">
            <span className="section-label">What We Do</span>
          </div>
          <h2
            className="font-serif reveal delay-1 mb-4"
            style={{
              fontSize: "clamp(2rem, 4vw, 3rem)",
              fontWeight: 700,
              letterSpacing: "-0.02em",
              lineHeight: 1.1,
            }}
          >
            Everything You Need.{" "}
            <span className="gradient-text italic">Nothing You Don't.</span>
          </h2>
          <p className="text-ink-muted text-lg max-w-xl mx-auto reveal delay-2" style={{ fontWeight: 400 }}>
            We cover every step of your journey, so nothing falls through the cracks.
          </p>
        </div>

        {/* Services grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services?.map((s, i) => (
            <div
              key={s?.step}
              className={`reveal delay-${Math.min(i + 1, 6)} group relative bg-parchment rounded-card p-8 border border-crimson-100 hover:border-crimson/30 transition-all duration-500 hover:shadow-crimson-sm hover:-translate-y-1`}
            >
              {/* Step number */}
              <span
                className="absolute top-6 right-6 font-serif text-5xl font-bold text-crimson/8 select-none pointer-events-none"
                style={{ lineHeight: 1 }}
              >
                {s?.step}
              </span>

              {/* Icon */}
              <div className="w-12 h-12 rounded-[10px] bg-crimson/8 flex items-center justify-center text-2xl mb-5 group-hover:scale-110 transition-transform duration-300">
                {s?.icon}
              </div>

              {/* Content */}
              <h3 className="font-serif text-xl font-semibold text-ink mb-1" style={{ letterSpacing: "-0.01em" }}>
                {s?.title}
              </h3>
              <p className="text-xs text-crimson font-semibold italic mb-3">{s?.tagline}</p>
              <p className="text-sm text-ink-muted leading-relaxed mb-5" style={{ fontWeight: 400 }}>
                {s?.desc}
              </p>

              {/* Highlight tag */}
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-crimson bg-crimson/8 px-3 py-1.5 rounded-full">
                <span className="w-1 h-1 rounded-full bg-crimson" />
                {s?.highlight}
              </span>

              {/* Hover accent line */}
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-crimson rounded-b-card scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-14 reveal delay-3">
          <a href="#contact" className="btn-primary">
            Book Your Free Strategy Session
          </a>
        </div>
      </div>
    </section>
  );
}